import React, { useEffect, useRef, useState, useCallback } from 'react';

const SummaryModal = ({ isOpen, onClose, isDark, toggleTheme }) => {
  const [progress, setProgress] = useState(0.72);
  const targetRef = useRef(0.72);
  const currentRef = useRef(0.72);
  const touchYRef = useRef(null);
  const touchXRef = useRef(null);
  const rafRef = useRef(null);
  const containerRef = useRef(null);

  const clamp = (v, a, b) => Math.max(a, Math.min(b, v));
  const lerp = (a, b, t) => a + (b - a) * t;
  const smooth = t => t * t * (3 - 2 * t);

  // Handle Escape key
  const handleKeyDown = useCallback((e) => {
    if (e.key === 'Escape') {
      onClose();
    } else if (e.key === 'ArrowRight' || e.key === 'ArrowDown') {
      targetRef.current = clamp(targetRef.current + 0.15, 0, 1);
    } else if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') {
      targetRef.current = clamp(targetRef.current - 0.15, 0, 1);
    }
  }, [onClose]);

  useEffect(() => {
    if (!isOpen) return;

    window.addEventListener('keydown', handleKeyDown);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    // Reset progress to Life DAG view initially
    targetRef.current = 0.72;
    currentRef.current = 0.72;
    setProgress(0.72);

    const STEP = 1 / 28;
    const TOUCH_STEP = 1 / 380;

    const onWheel = (e) => {
      e.preventDefault();
      e.stopPropagation();
      const dir = Math.sign(e.deltaY);
      const mag = clamp(Math.abs(e.deltaY) / 100, 0.4, 1.6);
      targetRef.current = clamp(targetRef.current + dir * STEP * mag, 0, 1);
    };

    const onTouchStart = (e) => {
      touchYRef.current = e.touches[0].clientY;
      touchXRef.current = e.touches[0].clientX;
    };

    const onTouchMove = (e) => {
      if (touchYRef.current == null) return;
      const y = e.touches[0].clientY;
      const x = e.touches[0].clientX;
      const dy = touchYRef.current - y;
      const dx = (touchXRef.current || x) - x;
      // Support both vertical scroll and horizontal swipe
      const delta = Math.abs(dy) > Math.abs(dx) ? dy : dx;
      touchYRef.current = y;
      touchXRef.current = x;
      targetRef.current = clamp(targetRef.current + delta * TOUCH_STEP, 0, 1);
    };

    const onTouchEnd = () => {
      touchYRef.current = null;
      touchXRef.current = null;
    };

    const container = containerRef.current;
    if (container) {
      container.addEventListener('wheel', onWheel, { passive: false });
      container.addEventListener('touchstart', onTouchStart, { passive: true });
      container.addEventListener('touchmove', onTouchMove, { passive: false });
      container.addEventListener('touchend', onTouchEnd, { passive: true });
    }

    const tick = () => {
      currentRef.current = lerp(currentRef.current, targetRef.current, 0.16);
      if (Math.abs(currentRef.current - targetRef.current) < 0.0005) {
        currentRef.current = targetRef.current;
      }
      setProgress(currentRef.current);
      rafRef.current = requestAnimationFrame(tick);
    };

    rafRef.current = requestAnimationFrame(tick);

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = prevOverflow;
      if (container) {
        container.removeEventListener('wheel', onWheel);
        container.removeEventListener('touchstart', onTouchStart);
        container.removeEventListener('touchmove', onTouchMove);
        container.removeEventListener('touchend', onTouchEnd);
      }
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, [isOpen, handleKeyDown]);

  if (!isOpen) return null;

  const p = progress;

  // Scene opacities
  const glyphOp = clamp(1 - (p - 0.22) / 0.22, 0, 1);
  const glyphScale = lerp(0.92, 1.0, smooth(clamp((0.40 - p) / 0.40, 0, 1)));
  const pipeOp = clamp((p - 0.22) / 0.22, 0, 1);

  // DAG nodes data for Sarthak's timeline
  const dagYears = [
    { x: 110, at: 0.35, label: '2003' },
    { x: 280, at: 0.45, label: '2019' },
    { x: 450, at: 0.55, label: '2021' },
    { x: 620, at: 0.62, label: '2022' },
    { x: 790, at: 0.70, label: '2024' },
    { x: 960, at: 0.76, label: 'now' },
    { x: 1120, at: 0.84, label: '?' },
  ];

  const dagNodes = [
    { x: 70, y: 160, w: 130, at: 0.35, label: 'start', status: 'ok' },
    { x: 225, y: 160, w: 145, at: 0.45, label: 'hello_world', status: 'ok' },
    { x: 395, y: 160, w: 145, at: 0.55, label: 'enroll.vit', status: 'ok' },
    { x: 565, y: 160, w: 145, at: 0.62, label: 'first_intern', status: 'ok' },
    { x: 735, y: 160, w: 145, at: 0.70, label: 'build_apps', status: 'ok' },
    { x: 905, y: 110, w: 145, at: 0.76, label: 'ship_things', status: 'ok' },
    { x: 905, y: 210, w: 145, at: 0.76, label: 'stay_curious', status: 'run' },
    { x: 1075, y: 160, w: 65, at: 0.84, label: '???', status: 'pending' },
  ];

  const dagEdges = [
    { x1: 200, y1: 180, x2: 225, y2: 180, at: 0.45 },
    { x1: 370, y1: 180, x2: 395, y2: 180, at: 0.55 },
    { x1: 540, y1: 180, x2: 565, y2: 180, at: 0.62 },
    { x1: 710, y1: 180, x2: 735, y2: 180, at: 0.70 },
    { x1: 880, y1: 180, x2: 905, y2: 130, at: 0.76 },
    { x1: 880, y1: 180, x2: 905, y2: 230, at: 0.76 },
    { x1: 1050, y1: 130, x2: 1075, y2: 180, at: 0.84 },
    { x1: 1050, y1: 230, x2: 1075, y2: 180, at: 0.84 },
  ];

  const packetT = clamp((p - 0.35) / 0.50, 0, 1);
  const packetX = lerp(100, 970, packetT);

  const handleProgressBarClick = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const clickP = clamp((e.clientX - rect.left) / rect.width, 0, 1);
    targetRef.current = clickP;
  };

  return (
    <div 
      id="summary-modal" 
      ref={containerRef}
      className="summary-modal-backdrop"
      role="dialog"
      aria-label="Sarthak Joshi Life DAG Timeline and Identity"
    >
      <div className="intro-bg"></div>
      <div className="intro-grid"></div>

      {/* Clean Top Bar with Tabs and Single Close Button */}
      <div className="summary-top-bar">
        <div className="summary-tabs-container">
          <button
            type="button"
            className={`summary-tab-btn ${p < 0.40 ? 'active' : ''}`}
            onClick={() => { targetRef.current = 0.0; }}
            title="View Identity"
          >
            Identity
          </button>
          <button
            type="button"
            className={`summary-tab-btn ${p >= 0.40 ? 'active' : ''}`}
            onClick={() => { targetRef.current = 0.85; }}
            title="View Life DAG"
          >
            Life DAG
          </button>
        </div>

        <button 
          type="button" 
          className="summary-close-btn"
          onClick={onClose}
          title="Close (Esc)"
          aria-label="Close"
        >
          ✕
        </button>
      </div>

      {/* Scene 1: Identity Glyph */}
      <div 
        className={`intro-scene scene-glyph${glyphOp > 0.01 ? ' on' : ''}`} 
        id="scene-glyph" 
        style={{ 
          opacity: glyphOp.toFixed(3), 
          pointerEvents: glyphOp > 0.5 ? 'auto' : 'none' 
        }}
      >
        <div className="glyph-wrap" id="glyph-wrap" style={{ transform: `scale(${glyphScale})` }}>
          <div className="glyph-meta top">{"// identity.resolved"}</div>
          <div className="glyph-at"><em>SJ</em><span className="glyph-dot">·</span></div>
          <div className="glyph-tag">Sarthak Joshi - Software Engineer</div>
          <div className="glyph-rule"></div>
          <div className="glyph-meta bot">FSD / SDE / DOE</div>
          <div className="glyph-sub">
            Full Stack Developer · Software Engineer · DevOps Engineer
          </div>
          <div className="glyph-cta-hint" onClick={() => { targetRef.current = 0.85; }}>
            Explore Life DAG Pipeline →
          </div>
        </div>
      </div>

      {/* Scene 2: Life DAG */}
      <div 
        className={`intro-scene scene-pipe${pipeOp > 0.01 ? ' on' : ''}`} 
        id="scene-pipe" 
        style={{ 
          opacity: pipeOp.toFixed(3),
          pointerEvents: pipeOp > 0.5 ? 'auto' : 'none'
        }}
      >
        <div className="pipe-title">{"// life.dag - run_id: since_2003 · state: "}<span className="lbl-ok">running</span></div>
        <svg className="pipe-svg" viewBox="0 0 1200 380" preserveAspectRatio="xMidYMid meet">
          <defs>
            <marker id="ah-summary" viewBox="0 0 8 8" refX="7" refY="4" markerWidth="6" markerHeight="6" orient="auto">
              <path d="M0 0 L8 4 L0 8 z" fill="currentColor" />
            </marker>
          </defs>
          <g className="pipe-rules">
            <line x1="60" x2="1140" y1="60" y2="60" className="rule" />
            <line x1="60" x2="1140" y1="320" y2="320" className="rule" />
            <text x="60" y="48" className="lane-label">timeline</text>
            <text x="1140" y="340" textAnchor="end" className="lane-label">→ present</text>
          </g>
          <g className="pipe-years">
            {dagYears.map((yr, i) => (
              <g key={i} className={`yr${p >= yr.at ? ' on' : ''}`}><text x={yr.x} y="88">{yr.label}</text></g>
            ))}
          </g>
          <g className="pipe-edges" stroke="currentColor" fill="none" strokeWidth="1.2">
            {dagEdges.map((e, i) => (
              <line key={i} className={`edge${p >= e.at ? ' on' : ''}`} x1={e.x1} y1={e.y1} x2={e.x2} y2={e.y2} markerEnd="url(#ah-summary)" />
            ))}
          </g>
          <g className="pipe-nodes airflow">
            {dagNodes.map((n, i) => (
              <g key={i} className={`node ${n.status}${p >= n.at ? ' on' : ''}`}>
                <rect x={n.x} y={n.y} width={n.w} height="40" rx="4" />
                <circle cx={n.x + 15} cy={n.y + 20} r="4" className="status" />
                <text x={n.x + 30} y={n.y + 25}>{n.label}</text>
              </g>
            ))}
          </g>
          <circle id="packet-summary" className="packet" r="3.5" cx={packetX} cy="180" style={{ opacity: pipeOp < 0.1 ? 0 : 0.95 }} />
        </svg>
        <div className="pipe-legend">
          <span className="lg"><span className="lg-dot ok"></span>success</span>
          <span className="lg"><span className="lg-dot run"></span>running</span>
          <span className="lg"><span className="lg-dot pending"></span>scheduled</span>
          <span className="lg muted">tasks 8 · failed 0 · retries: who's counting</span>
        </div>
      </div>

      {/* Bottom progress bar & scrubber */}
      <div className="intro-progress">
        <div 
          className="intro-progress-bar" 
          id="summary-bar" 
          style={{ '--p': `${(p * 100).toFixed(1)}%`, cursor: 'pointer' }}
          onClick={handleProgressBarClick}
          title="Click to scrub timeline"
        ></div>
        <div className="intro-progress-ticks">
          <span className="tick" style={{ left: '0%' }} onClick={() => { targetRef.current = 0.0; }}></span>
          <span className="tick" style={{ left: '50%' }} onClick={() => { targetRef.current = 0.5; }}></span>
          <span className="tick" style={{ left: '100%' }} onClick={() => { targetRef.current = 1.0; }}></span>
        </div>
        <div className="intro-progress-labels">
          <span onClick={() => { targetRef.current = 0.0; }} style={{ cursor: 'pointer' }}>identity</span>
          <span onClick={() => { targetRef.current = 0.5; }} style={{ cursor: 'pointer' }}>pipeline scrub</span>
          <span onClick={() => { targetRef.current = 1.0; }} style={{ cursor: 'pointer' }}>present (2026)</span>
        </div>
      </div>
    </div>
  );
};

export default SummaryModal;
